import React, { useEffect, useState } from 'react';
import { apiGet } from '../../api';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import Pagination from '../../components/Pagination';

const labelCls = 'block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5';
const inputCls = 'w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30';

function getDefaultDates() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

const UserStatement = () => {
  const { username } = useParams();
  const [statement, setStatement] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [filters, setFilters] = useState({ ...getDefaultDates(), order: 'desc', wallet_type: 'GUSD' });
  const [useDates, setUseDates] = useState(false);
  const pageSize = 15;

  const fetchStatement = async (pageNum = 1, filterVals = filters) => {
    setLoading(true);
    try {
      let query = `/api/admin/user/${username}/statement?page=${pageNum}&pageSize=${pageSize}`;
      if (filterVals.order) query += `&order=${filterVals.order}`;
      query += `&wallet_type=${filterVals.wallet_type}`;
      if (useDates) query += `&startDate=${filterVals.startDate}&endDate=${filterVals.endDate}`;
      const res = await apiGet(query, {});
      setStatement(res.data.data.data);
      setTotalPages(res.data.data.totalPages);
      setTotalRows(res.data.data.totalRows || 0);
    } catch (err) {
      toast.error('Failed to fetch statement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatement(page, filters);
    // eslint-disable-next-line
  }, [page, filters, useDates, username]);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setPage(1);
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-serif italic text-2xl sm:text-3xl text-foreground mb-1">Wallet Statement</h1>
        <p className="text-sm text-muted-foreground font-mono">{username}</p>
      </div>

      <div className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className={labelCls}>Wallet Type</label>
            <select name="wallet_type" value={filters.wallet_type} onChange={handleFilterChange} className={inputCls}>
              <option value="GUSD">GUSD</option>
              <option value="BFM">BFM</option>
              <option value="APP_CREDIT">APP Credit</option>
              <option value="FOREX">FOREX</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Order</label>
            <select name="order" value={filters.order} onChange={handleFilterChange} className={inputCls}>
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={useDates}
                onChange={(e) => { setUseDates(e.target.checked); setPage(1); }}
                className="h-4 w-4 rounded border-border/70 accent-ginox-green"
              />
              <span className="text-xs text-muted-foreground">Filter by Date</span>
            </label>
          </div>
        </div>
        {useDates && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div>
              <label className={labelCls}>Start Date</label>
              <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange}
                max={filters.endDate} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>End Date</label>
              <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange}
                min={filters.startDate} max={new Date().toISOString().slice(0, 10)} className={inputCls} />
            </div>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              {['Tranx ID', 'Type', 'Wallet', 'Amount', 'Total', 'Message', 'Date'].map((h) => (
                <th key={h} className="border-b border-border/70 px-4 py-3 text-[10px] font-semibold uppercase tracking-[1px] text-muted-foreground text-left">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="py-12 text-center text-sm text-muted-foreground">Loading...</td></tr>
            ) : statement.length === 0 ? (
              <tr><td colSpan={7} className="py-12 text-center text-sm text-muted-foreground">No statement found</td></tr>
            ) : statement.map((s) => (
              <tr key={s._id} className="group hover:bg-white/[0.02] transition-colors">
                <td className="border-b border-border/40 px-5 py-3.5 text-sm font-mono text-muted-foreground">{s.tranx_id}</td>
                <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">{s.tranx_type}</td>
                <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">{s.wallet_type}</td>
                <td className="border-b border-border/40 px-5 py-3.5 text-sm font-mono text-foreground">{s.amount}</td>
                <td className="border-b border-border/40 px-5 py-3.5 text-sm font-mono text-foreground">{s.total}</td>
                <td className="border-b border-border/40 px-5 py-3.5 text-sm text-muted-foreground max-w-[200px] truncate">{s.message}</td>
                <td className="border-b border-border/40 px-5 py-3.5 text-sm font-mono text-muted-foreground">
                  {new Date(s.createdAt).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="border-t border-white/[0.08] px-4">
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalRows={totalRows}
          pageSize={pageSize}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
};

export default UserStatement;
